import { App, Plugin } from 'vue';
import { Router } from 'vue-router';
import { DateTime } from 'luxon';
import { FormType } from '@/helpers/Form';
import { Columns } from '@/helpers/Interfaces';
import Pager, { instanceOfPagerContract } from '@/helpers/Pager';
import { useViewStateStore, ViewStateStore } from '@/store/viewstate';
import { FilterState } from '@/modules/core/common/services/FilterService';
import { TimeRange, TimeScale } from '@/modules/low-code/components/helpers/TimetableDataHelper';
import { createViewStateKey } from '@/utils/viewStateKey';
import cloneDeep from 'lodash/cloneDeep';
import merge from 'lodash/merge';

let currentInstance: ViewState | null = null;

export const restoreState = (key: string, filter?: FormType<any>, pager?: Pager, columns?: Columns, settings?: Settings): void =>
{
    return currentInstance?.restore(key, filter, pager, columns, settings);
};

export const persistState = (key: string, filter?: FormType<any>, pager?: Pager, columns?: Columns, settings?: Settings): void =>
{
    return currentInstance?.persist(key, filter, pager, columns, settings);
};

export const setFilterState = (state: FilterState): void =>
{
    return currentInstance?.setFilterState(state);
};

export const getFilterState = (): FilterState =>
{
    return currentInstance?.getFilterState();
};

export const setColumnsState = (state: Columns): void =>
{
    return currentInstance?.setColumnsState(state);
};

export const getColumnsState = (): Columns =>
{
    return currentInstance?.getColumnsState();
};

export const setSettings = (settings: Settings): void =>
{
    return currentInstance?.setSettings(settings);
};

export const getSettings = (): Settings =>
{
    return currentInstance?.getSettings();
};

interface ViewState
{
    restore(key: string, filter?: FormType<any>, pager?: Pager, columns?: Columns, settings?: any): void;
    persist(key: string, filter?: FormType<any>, pager?: Pager, columns?: Columns, settings?: any): void;
    setFilterState(state: FilterState): void;
    getFilterState(): FilterState;
    setColumnsState(state: Columns): void;
    getColumnsState(): Columns;
    setSettings(settings: any): void;
    getSettings(): any;
}

function isObjectEmpty(object: any)
{
    for (const prop in object)
    {
        if (object.hasOwnProperty(prop))
        {
            return false;
        }
    }

    return true;
}

export interface Settings {
    start: string;
    end: string;
    view?: string;
    isActiveWorkHours?: boolean;
    year?: DateTime;
    period?: string;
    timeRange?: TimeRange;
    timeScale?: TimeScale;
}

export class ViewStateOptions
{
    public router: Router;
}

class ViewStateHelper implements ViewState
{
    private options: ViewStateOptions;
    private store: ViewStateStore;

    public constructor(app: App<any>, options: ViewStateOptions, store: ViewStateStore)
    {
        this.options = options;
        this.store = store;
    }

    private routeKey(): string
    {
        const currentRoute = this.options.router.currentRoute.value;
        const routeName = currentRoute.name?.toString();

        return createViewStateKey({
            routeName,
            routeParams: currentRoute.params as Record<string, unknown>,
            endpoint: currentRoute.path,
        });
    }

    public restore(key: string, filter?: FormType<any>, pager?: Pager, columns?: Columns, settings?: any): void
    {
        const nextKey = key || this.routeKey();
        this.store.key = nextKey;

        const currentRoute = this.options.router.currentRoute.value;
        const legacyKey = `${currentRoute.name?.toString() || ''}${Object.values(currentRoute.params).join('-') ? `-${Object.values(currentRoute.params).join('-')}` : ''}`;

        const filterState = this.store.getFilters(this.store.key);
        const pagerState = this.store.getPager(this.store.key);
        const columnsState = this.store.getColumns(this.store.key);
        const settingsState = this.store.getSettings(this.store.key);
        const shouldTryLegacy = this.store.key !== legacyKey;

        const legacyFilterState = shouldTryLegacy ? this.store.getFilters(legacyKey) : null;
        const legacyPagerState = shouldTryLegacy ? this.store.getPager(legacyKey) : null;
        const legacyColumnsState = shouldTryLegacy ? this.store.getColumns(legacyKey) : null;
        const legacySettingsState = shouldTryLegacy ? this.store.getSettings(legacyKey) : null;

        const resolvedFilterState = (filterState?.form || filterState?.activeTemplate?.contentJson) ? filterState : legacyFilterState;
        const resolvedPagerState = instanceOfPagerContract(pagerState) ? pagerState : legacyPagerState;
        const resolvedColumnsState = (!isObjectEmpty(columnsState.visible) || !isObjectEmpty(columnsState.positions)) ? columnsState : legacyColumnsState;
        const resolvedSettingsState = !isObjectEmpty(settingsState) ? settingsState : legacySettingsState;

        if (filter != undefined)
        {
            const rawForm = resolvedFilterState?.form || JSON.parse(resolvedFilterState?.activeTemplate?.contentJson || '{}');
            const formData = typeof rawForm === 'object' && rawForm !== null ? rawForm : {};
            // Keep reset semantics: $initial stays defaults, persisted values are applied only as current data.
            const filterInternal = filter as FormType<any> & { $initial?: Record<string, unknown> };
            const existingInitial = filterInternal.$initial ? cloneDeep(filterInternal.$initial) : {};
            const defaults = merge({}, existingInitial);

            Object.keys(formData).forEach((key) =>
            {
                if (!(key in defaults)) defaults[key] = null;
            });

            const current = merge({}, defaults, formData);

            filter.setInitialValues(defaults);
            filter.withData(current);
        }

        if (pager != undefined && instanceOfPagerContract(resolvedPagerState))
        {
            pager.apply(resolvedPagerState);
        }

        if (columns != undefined)
        {
            if (!isObjectEmpty(resolvedColumnsState?.visible || {}))
                columns.visible = resolvedColumnsState.visible;

            if (!isObjectEmpty(resolvedColumnsState?.positions || {}))
                columns.positions = resolvedColumnsState.positions;
        }

        if (settings != undefined)
        {
            settings.view = resolvedSettingsState?.view;
            settings.start = resolvedSettingsState?.start;
            settings.end = resolvedSettingsState?.end;
            settings.isActiveWorkHours = resolvedSettingsState?.isActiveWorkHours;
            settings.year = resolvedSettingsState?.year;
            settings.period = resolvedSettingsState?.period;
            settings.timeRange = resolvedSettingsState?.timeRange ?? settings.timeRange;
            settings.timeScale = resolvedSettingsState?.timeScale ?? settings.timeScale;
        }

        if (shouldTryLegacy && (
            resolvedFilterState === legacyFilterState
            || resolvedPagerState === legacyPagerState
            || resolvedColumnsState === legacyColumnsState
            || resolvedSettingsState === legacySettingsState
        ))
        {
            this.persist(this.store.key, filter, pager, columns, settings);
        }
    }

    public persist(key: string, filter?: FormType<any>, pager?: Pager, columns?: Columns, settings?: any): void
    {
        this.store.key = key || this.routeKey();

        if (filter != undefined)
        {
            const filterState = this.store.getFilters(this.store.key);

            this.store.setFilters(this.store.key, {
                activeTemplate: filterState.activeTemplate,
                visible: filterState.visible,
                form: filter.data()
            });
        }

        if (pager != undefined)
        {
            this.store.setPager(this.store.key, pager.data());
        }

        if (columns != undefined)
        {
            this.store.setColumns(this.store.key, JSON.parse(JSON.stringify(columns)));
        }

        if (settings != undefined)
        {
            this.store.setSettings(this.store.key, settings);
        }
    }

    public getFilterState(): FilterState
    {
        return this.store.getFilters(this.store.key || this.routeKey());
    }

    public setFilterState(state: FilterState): void
    {
        this.store.setFilters(this.store.key || this.routeKey(), state);
    }

    public getColumnsState(): Columns
    {
        return this.store.getColumns(this.store.key || this.routeKey());
    }

    public setColumnsState(state: Columns): void
    {
        this.store.setColumns(this.store.key || this.routeKey(), JSON.parse(JSON.stringify(state)));
    }

    public getSettings(): Settings
    {
        return this.store.getSettings(this.store.key || this.routeKey());
    }

    public setSettings(settings: Settings): void
    {
        this.store.setSettings(this.store.key || this.routeKey(), settings);
    }
}

const ViewStatePlugin: Plugin =
{
    install(app, options)
    {
        if (!options || !options.router)
        {
            throw new Error("ViewStateOptions.router must be set.");
        }

        currentInstance = new ViewStateHelper(app, options, useViewStateStore());
    }
};

export default ViewStatePlugin;
